import { useAtomValue } from 'jotai';

import { Box,Divider, Stack } from '@ekhodealer/ekho-common/rev';

import type { LeadDetails } from '@ekhodealer/ekho-common';

import { useGetLeadDetails } from '@common/api/leads/useGetLeadDetails';
import { AccordionSection } from '@common/components/AccordionSection';
import { LenderDecisionsList } from '@common/components/LenderDecisionsList';

import { leadDetailsDrawerAtom } from '../../atoms/leadDetailsDrawerAtoms';

import { LeadsVehicle } from './shared/LeadsVehicle';

const SECTION_TITLE = 'Rejected financing';

interface RejectedFinancingItemProps {
	item: LeadDetails['rejectedFinancing'][number];
}

/**
 * Individual rejected financing item display component
 * Shows vehicle details and lender decisions
 */
const RejectedFinancingItem = ({ item }: RejectedFinancingItemProps) => {
	const { vehicle, lenderDecisions } = item;

	return (
		<Box rounding='12' background='secondary' padding='16'>
			<Stack itemsSpacing='12'>
				{/* Vehicle Info Box */}
				<LeadsVehicle vehicle={vehicle} />

				{/* Lender Decisions */}
				{lenderDecisions.length > 0 && (
					<Box rounding='12' padding='12' background='primary'>
						<LenderDecisionsList lenderDecisions={lenderDecisions} />
					</Box>
				)}
			</Stack>
		</Box>
	);
};

export const RejectedFinancingSection = () => {
	const { selectedRow } = useAtomValue(leadDetailsDrawerAtom);
	const { data: leadDetails } = useGetLeadDetails(selectedRow?.id);

	if (!leadDetails || !selectedRow?.id) {
		return null;
	}

	const { rejectedFinancing } = leadDetails;

	// Hide section if no rejected financing
	if (rejectedFinancing.length === 0) {
		return null;
	}

	const sectionTitle = `${SECTION_TITLE} (${rejectedFinancing.length})`;

	return (
		<>
			<Divider />
			<AccordionSection title={sectionTitle} defaultExpanded>
				<Stack itemsSpacing='12'>
					{rejectedFinancing.map((item, index) => (
						<RejectedFinancingItem key={index} item={item} />
					))}
				</Stack>
			</AccordionSection>
		</>
	);
};
